/** The columns of a type (1.N-2 deliverable 4), mirroring 1.U deliverable 1. Pure. */
import type { CatalogueDocumentColumn, DocTypeV1, FieldKind } from '@codex/contracts';

export type ColumnKind = FieldKind | CatalogueDocumentColumn;

export interface ColumnDef {
  readonly key: string;
  readonly label: string;
  readonly kind: ColumnKind;
  /** A select column's options, in the GM's order (the filter dialog offers them). */
  readonly options?: readonly string[];
}

const DOCUMENT_LABELS: Readonly<Record<CatalogueDocumentColumn, string>> = {
  title: 'Title',
  canonStatus: 'Canon status',
  playState: 'Play state',
  discovery: 'Discovery',
  updatedAt: 'Updated',
};

const RESERVED = new Set<string>(Object.keys(DOCUMENT_LABELS));

/**
 * The document columns (`playState` only for a playable type, `discovery` only for a
 * discoverable one), then the type's live fields, then live global fields for a canon type.
 * A field keyed like a document column is left out; of two fields with one key the first wins.
 */
export function resolveColumns(type: DocTypeV1, types: readonly DocTypeV1[]): ColumnDef[] {
  const out: ColumnDef[] = [];
  const doc = (key: CatalogueDocumentColumn) =>
    out.push({ key, label: DOCUMENT_LABELS[key], kind: key });
  doc('title');
  doc('canonStatus');
  if (type.capabilities.includes('playable')) doc('playState');
  if (type.capabilities.includes('discoverable')) doc('discovery');
  doc('updatedAt');
  const seen = new Set<string>(RESERVED);
  const add = (field: DocTypeV1['fields'][number]) => {
    if (field.archived || seen.has(field.key)) return;
    seen.add(field.key);
    const options =
      field.kind === 'select' || field.kind === 'multi_select' ? field.config.options : undefined;
    out.push({
      key: field.key,
      label: field.label,
      kind: field.kind,
      ...(options === undefined ? {} : { options }),
    });
  };
  for (const field of type.fields) if (field.docTypeId === type.id) add(field);
  if (type.library === 'canon') {
    const globals = new Map<string, DocTypeV1['fields'][number]>();
    for (const other of [type, ...types]) {
      for (const field of other.fields) {
        if (field.docTypeId === null && !globals.has(field.id)) globals.set(field.id, field);
      }
    }
    for (const field of globals.values()) add(field);
  }
  return out;
}

/** `title`, then every field column in field order; the document columns are choosable. */
export function defaultColumnKeys(columns: readonly ColumnDef[]): string[] {
  return columns
    .filter((column) => column.key === 'title' || !RESERVED.has(column.key))
    .map((column) => column.key);
}

/** The columns the view shows, in the view's order. */
export function visibleColumns(
  columns: readonly ColumnDef[],
  keys: readonly string[],
): ColumnDef[] {
  const byKey = new Map(columns.map((column) => [column.key, column]));
  return keys.flatMap((key) => {
    const found = byKey.get(key);
    return found ? [found] : [];
  });
}
